/**
 * TeamCard - card for displaying a team in the company dashboard
 *
 * Shows team name, member count, a mini radar chart of the team centroid,
 * and a primary (nearest) type badge.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import RadarChart from './RadarChart';

/* ------------------------------------------------------------------ */
/*  Styled components                                                  */
/* ------------------------------------------------------------------ */

const Card = styled.div`
  background: ${(props) => props.theme.colors.bgTertiary};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: ${(props) => props.theme.borderRadius.lg};
  padding: 24px;
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.15s;

  &:hover {
    border-color: ${(props) =>
      props.$clickable ? props.theme.colors.primary : props.theme.colors.border};
    box-shadow: ${(props) =>
      props.$clickable ? props.theme.shadows.soft : 'none'};
    transform: ${(props) => (props.$clickable ? 'translateY(-2px)' : 'none')};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const TitleGroup = styled.div`
  flex: 1;
  min-width: 0;
`;

const TeamName = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
  margin: 0 0 4px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MemberCount = styled.span`
  font-size: 0.8rem;
  color: ${(props) => props.theme.colors.textSecondary};
`;

const TypeBadge = styled(Link)`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  border-radius: ${(props) => props.theme.borderRadius.full};
  background: ${(props) => props.theme.colors.primaryGlow};
  border: 1px solid ${(props) => props.theme.colors.primary};
  font-family: ${(props) => props.theme.fonts.mono};
  font-size: 0.78rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.primary};
  margin-left: 12px;
  text-decoration: none;
  &:hover { opacity: 0.8; }
`;

const ChartWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;


/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const AXIS_LABELS = ['行動特性', '判断基準', '価値観', '共感性', '自己認識'];
const AXIS_KEYS = ['activation', 'judgment', 'choice', 'resonance', 'awareness'];

/** Convert a centroid object to RadarChart data array. */
function centroidToRadarData(centroid) {
  if (!centroid) return [];
  return AXIS_KEYS.map((key, i) => ({
    label: AXIS_LABELS[i],
    value: Math.min(Math.max(centroid[key] ?? 0, 0), 1),
  }));
}


/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

function TeamCard({ team = {}, analysis = {}, onClick }) {
  const { name = 'Unnamed Team', member_count, members } = team;
  const { centroid, nearest_type } = analysis;

  const count = member_count ?? (members ? members.length : 0);
  const radarData = centroidToRadarData(centroid);

  return (
    <Card $clickable={!!onClick} onClick={onClick}>
      <Header>
        <TitleGroup>
          <TeamName>{name}</TeamName>
          <MemberCount>
            {count} {count === 1 ? 'member' : 'members'}
          </MemberCount>
        </TitleGroup>

        {nearest_type && <TypeBadge to={`/types/${nearest_type}`} onClick={e => e.stopPropagation()}>{nearest_type}</TypeBadge>}
      </Header>

      {radarData.length === 5 && (
        <ChartWrapper>
          <RadarChart
            data={radarData}
            size={240}
            showLabels={true}
          />
        </ChartWrapper>
      )}

    </Card>
  );
}

export default TeamCard;
